import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { api } from '../lib/api';
import { downloadDocument } from '../lib/downloadDocument';
import { Badge, Button } from '../atoms';
import { DataTable } from './DataTable';
import { DocumentUploadModal } from './DocumentUploadModal';
import { useToast } from './Toast';
import { formatDate } from '../lib/orgTimezone';
import { useEntityPermission } from '../lib/usePermissions';

/**
 * EntityDocumentsPanel — per-entity documents list + upload + download for a
 * record detail page. Scopes documents by a single foreign-key column that the
 * `documents` table already carries (e.g. assessor_id / collector_id):
 *   - list     → GET  /api/documents?<fkColumn>=<entityId>   (generic CRUD FK filter)
 *   - upload   → POST /api/documents/upload  (DocumentUploadModal, FK pre-linked)
 *   - download → GET  /api/documents/:id/download
 *
 * Reuses the shared DocumentUploadModal + download path (same pattern as the
 * client/parcel Documents surfaces). No schema change — the FK column must
 * already exist on `documents`.
 */

interface EntityDocument {
  id: string;
  name: string;
  document_type: string | null;
  year: number | null;
  file_size_bytes: number | null;
  status: string | null;
  created_at: string | null;
}

type DocumentFkColumn = 'assessor_id' | 'collector_id';

function fmtBytes(bytes: number | null): string {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export interface EntityDocumentsPanelProps {
  fkColumn: DocumentFkColumn;
  entityId: string;
}

export function EntityDocumentsPanel({ fkColumn, entityId }: EntityDocumentsPanelProps) {
  const [uploadOpen, setUploadOpen] = useState(false);
  const { toast } = useToast();
  // Upload POSTs /api/documents/upload → gate on document.can_add (download stays open).
  const canAdd = useEntityPermission('document', 'can_add');

  // Namespaced under ['documents', …] so the shared DocumentUploadModal's
  // onSuccess invalidation of ['documents'] refreshes this list automatically.
  const docsQuery = useQuery<EntityDocument[]>({
    queryKey: ['documents', fkColumn, entityId],
    queryFn: ({ signal }) => api.get<EntityDocument[]>(`/api/documents?${fkColumn}=${entityId}`, signal),
    enabled: !!entityId,
  });

  const columns = useMemo<ColumnDef<EntityDocument>[]>(() => [
    { accessorKey: 'name', header: 'Name' },
    { accessorKey: 'document_type', header: 'Type', cell: ({ getValue }) => {
      const v = getValue() as string | null; return v ? <Badge variant="neutral">{v}</Badge> : '—';
    } },
    { accessorKey: 'year', header: 'Year', cell: ({ getValue }) => (getValue() as number | null) ?? '—' },
    { accessorKey: 'file_size_bytes', header: 'Size', cell: ({ getValue }) => fmtBytes(getValue() as number | null) },
    { accessorKey: 'status', header: 'Status', cell: ({ getValue }) => <Badge variant="neutral">{(getValue() as string) || 'Received'}</Badge> },
    { accessorKey: 'created_at', header: 'Uploaded', cell: ({ getValue }) => formatDate(getValue() as string | null, { dateOnly: true }) },
    { id: 'download', header: '', cell: ({ row }) => (
      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); downloadDocument(row.original.id, (m) => toast({ variant: 'error', message: m })); }}>Download</Button>
    ) },
  ], [toast]);

  const docs = docsQuery.data ?? [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
      {canAdd && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="primary" size="sm" onClick={() => setUploadOpen(true)}>Upload document</Button>
        </div>
      )}

      {docsQuery.isLoading ? (
        <p style={{ color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>Loading documents…</p>
      ) : docsQuery.error ? (
        <div style={{
          padding: 'var(--sp-4)', background: 'var(--color-status-red-bg)',
          border: '1px solid var(--color-status-red)', color: 'var(--color-status-red)',
          borderRadius: 'var(--r-lg)',
        }}>{(docsQuery.error as Error).message}</div>
      ) : docs.length === 0 ? (
        <p style={{ color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>
          No documents on this record yet. Use “Upload document” to add one.
        </p>
      ) : (
        <DataTable data={docs} columns={columns} />
      )}

      <DocumentUploadModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        defaultContext={{ [fkColumn]: entityId }}
      />
    </div>
  );
}
